import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import Footer from '../../components/common/Footer';
import HeaderLp from '../lp-1/HeaderLp';
import imgPath from '../../assets/images/HomeBannerMachine.png';
import './annualScan.css';
import {
  extractPrebookingJwt,
  fetchPrebooking,
  isPrebookingDobVerified,
  markPrebookingDobVerified,
  startPrebookingCheckout,
  unlockPrebooking,
  PrebookingPayload,
} from '../../api/gsPrebookings';

const emptyPrebooking: PrebookingPayload = {
  first_name: '',
  surname: '',
  paid_amount: 50,
  aftercare_first_name: '',
  offer_expires_on: '',
  offer_expired: false,
  paid: false,
  location: '',
};

const formatDobInput = (raw: string) => {
  const digits = String(raw || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const isValidDobFormat = (value: string) => /^\d{2}\/\d{2}\/\d{4}$/.test(String(value || '').trim());

const amountLabel = (value: number | string | undefined) =>
  value === 0 || value ? String(value) : '50';

const AnnualScan: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsDob, setNeedsDob] = useState(false);
  const [dob, setDob] = useState('');
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [token, setToken] = useState(() => extractPrebookingJwt(searchParams.get('token')));
  const [data, setData] = useState<PrebookingPayload>(emptyPrebooking);

  const applyPrebooking = useCallback((payload: PrebookingPayload) => {
    setData({ ...emptyPrebooking, ...payload });
    setNeedsDob(false);
    setError(null);
  }, []);

  const loadPrebooking = useCallback(
    async (jwt: string) => {
      if (!jwt) {
        setLoading(false);
        setError(
          'This page requires a valid private link. Please use the link from your email, or contact us for a new one.'
        );
        return;
      }

      setLoading(true);
      setError(null);

      if (!isPrebookingDobVerified(jwt)) {
        setNeedsDob(true);
        setError('Enter your date of birth to view your annual scan offer.');
        setLoading(false);
        return;
      }

      const { ok, status, body } = await fetchPrebooking(jwt);

      if (ok && body?.success && body.data) {
        applyPrebooking(body.data);
        setLoading(false);
        return;
      }

      if (body?.error_code === 'dob_required' || status === 410 || body?.error_code === 'link_expired') {
        setNeedsDob(true);
        setError(body?.error || 'Enter your date of birth to view your annual scan offer.');
        setLoading(false);
        return;
      }

      setError(body?.error || 'This link is not valid. Please check the URL or contact us for a new link.');
      setLoading(false);
    },
    [applyPrebooking]
  );

  useEffect(() => {
    const jwt = extractPrebookingJwt(searchParams.get('token'));
    setToken(jwt);
    loadPrebooking(jwt);
  }, [searchParams, loadPrebooking]);

  const handleUnlock = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token) return;

    const dobValue = dob.trim();
    if (!isValidDobFormat(dobValue)) {
      setUnlockError('Please enter your date of birth in DD/MM/YYYY format.');
      return;
    }

    setUnlockLoading(true);
    setUnlockError(null);
    const { ok, body } = await unlockPrebooking(token, dobValue);
    setUnlockLoading(false);

    if (ok && body?.success && body.data) {
      const verifiedToken = body.access_token || token;
      markPrebookingDobVerified(verifiedToken, body.data.uuid);
      if (body.access_token) {
        const next = new URLSearchParams(searchParams);
        next.set('token', body.access_token);
        setSearchParams(next, { replace: true });
        setToken(body.access_token);
      }
      applyPrebooking(body.data);
      return;
    }

    setUnlockError(body?.error || 'Unable to verify date of birth.');
  };

  const handlePayDeposit = async (event: React.MouseEvent) => {
    event.preventDefault();

    if (data.paid) {
      window.location.href = `/annual-scan/payment-successful?uuid=${encodeURIComponent(data.uuid || '')}`;
      return;
    }

    if (!token) {
      setError('Missing access token. Please reopen your private link.');
      return;
    }

    setCheckoutLoading(true);
    const { ok, body } = await startPrebookingCheckout(token, { landing: 'corevitals' });
    setCheckoutLoading(false);

    if (ok && body?.data?.already_paid && body.data.redirect_url) {
      window.location.href = body.data.redirect_url;
      return;
    }

    if (ok && body?.data?.checkout_url) {
      window.location.href = body.data.checkout_url;
      return;
    }

    setError(body?.error || 'Unable to start payment. Please try again.');
  };

  const depositAmount = amountLabel(data.paid_amount);
  const aftercareName = data.aftercare_first_name || 'your aftercare manager';
  const expiryLabel = data.offer_expires_on || 'in 7 days';
  const siteName = data.location || 'the location';

  if (loading) {
    return (
      <div>
        <HeaderLp />
        <div className="annual-scan-state">
          <p>Loading your offer…</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (error && !needsDob && !data.uuid) {
    return (
      <div>
        <HeaderLp />
        <div className="annual-scan-state">
          <h1>This link has expired or is invalid.</h1>
          <p>{error}</p>
          <p>
            Need help? Email <a href="mailto:care@getscanned.me">care@getscanned.me</a> or call 020 4592 0454.
          </p>
        </div>
        <Footer />
      </div>
    );
  }

  if (needsDob) {
    return (
      <div>
        <HeaderLp />
        <div className="annual-scan-state">
          <h1>{error || 'Enter your date of birth (DD/MM/YYYY) to view your annual scan offer.'}</h1>
          <form className="annual-scan-unlock" onSubmit={handleUnlock}>
            <input
              type="text"
              placeholder="DD/MM/YYYY"
              inputMode="numeric"
              maxLength={10}
              autoComplete="bday"
              value={dob}
              onChange={(e) => setDob(formatDobInput(e.target.value))}
              required
            />
            {unlockError && <p className="annual-scan-error">{unlockError}</p>}
            <button type="submit" className="btnPrimary" disabled={unlockLoading}>
              {unlockLoading ? 'Checking…' : 'Continue'}
            </button>
          </form>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <HeaderLp />
      {error && <p className="annual-scan-error annual-scan-inline-error">{error}</p>}
      <div className="repeat-scan-section">
        <div className="repeat-scan-intro">
          <h1>
            Save on your next <span>full-body MRI</span>
          </h1>
          <p>
            Book another Full Body MRI within the next 18 months and receive an exclusive repeat-scan price.
          </p>
        </div>

        <div className="container1440">
          <div className="repeat-scan-offer">
            <h3>Your repeat-scan offer</h3>
            <div className="offer-price">
              £1,400 <span>£1,650</span>
            </div>
            <p className="offer-saving">Save £250</p>
            <img src={imgPath} alt="" width="318" />
            <div className="offer-details">
              {data.paid ? (
                <p className="deposit-text">
                  Your £{depositAmount} deposit has been paid. {aftercareName} will contact you to arrange your
                  scan.
                </p>
              ) : (
                <p className="deposit-text">
                  Secure your offer with a £{depositAmount} non-refundable deposit.
                </p>
              )}
              <div>
                <button
                  type="button"
                  className="btnPrimary"
                  onClick={handlePayDeposit}
                  disabled={checkoutLoading || (Boolean(data.offer_expired) && !data.paid)}
                >
                  {checkoutLoading
                    ? 'Starting checkout…'
                    : data.paid
                      ? 'Deposit paid'
                      : data.offer_expired
                        ? 'Offer expired'
                        : `Pay £${depositAmount} deposit`}
                </button>
              </div>
              <p className="offer-note">
                {data.paid
                  ? 'The deposit will be deducted from your final balance.'
                  : 'Pay your deposit within the next 7 days to secure the discounted price. The deposit will be deducted from your final balance.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <section className="choose-location">
        <h3>Choose where you scan</h3>
        <div className="location-info">
          <p>
            Return to {siteName}, where you had your first scan, or choose any available centre across our
            network.
          </p>
          <p>Once you&apos;ve paid your deposit, {aftercareName} will contact you to arrange your scan.</p>
          <p>
            Offer ends {expiryLabel}. Your repeat scan must be completed within 18 months of your first scan.
          </p>
        </div>
        <p className="terms">
          Terms: Deposits are non-refundable. The deposit must be paid within seven days to secure the
          discounted price and will be deducted from the final scan balance. This offer cannot be transferred
          or combined with another promotion.
        </p>
      </section>
      <Footer />
    </div>
  );
};

export default AnnualScan;
